import type { BiomapHabitatClassification } from '../../interfaces/biomap/IBiomapLayer';
import type {
  IBiomapInterventionAvailability,
  IBiomapInterventionTarget,
} from '../../interfaces/strategic-view/IBiomapInterventionAssessment';
import type { IRasterLayer } from '../../interfaces/sjmap2/raster/IRaster';
import type { IRasterSource } from '../../interfaces/sjmap2/raster/IRasterSource';
import type {
  Feature,
  FeatureCollection,
  GeoJsonObject,
  GeometryCollection,
  MultiPolygon,
  Polygon,
} from 'geojson';
import type { BiomapInterventionGeometry } from '../../interfaces/strategic-view/IBiomapInterventionAssessment';
import { getBiomapRasterIdentity } from './biomap-layer.utils';
import { getRasterSourceUUID } from '../../types/strategic/strategic.types';

export interface BiomapInterventionResolvedTargets {
  availability: IBiomapInterventionAvailability;
  habitat: Partial<Record<BiomapHabitatClassification, IBiomapInterventionTarget>>;
  msa?: IBiomapInterventionTarget;
}

export function resolveBiomapInterventionTargets(
  layers: readonly IRasterLayer[],
  sourceBefore: IRasterSource | null | undefined,
  sourceAfter: IRasterSource | null | undefined,
): BiomapInterventionResolvedTargets {
  const result: BiomapInterventionResolvedTargets = {
    availability: {
      habitat: false,
      habitatL1: false,
      habitatL2: false,
      msa: false,
    },
    habitat: {},
  };

  if (!sourceBefore || !sourceAfter) {
    return result;
  }

  layers.forEach((layer) => {
    const identity = getBiomapRasterIdentity(layer);

    if (!identity) {
      return;
    }

    const before = findMatchingSource(layer.sources ?? [], sourceBefore);
    const after = findMatchingSource(layer.sources ?? [], sourceAfter);
    const sourceBeforeUuid = getRasterSourceUUID(before);
    const sourceAfterUuid = getRasterSourceUUID(after);

    if (
      !before ||
      !after ||
      !sourceBeforeUuid ||
      !sourceAfterUuid ||
      sourceBeforeUuid === sourceAfterUuid
    ) {
      return;
    }

    const target: IBiomapInterventionTarget = {
      rasterLayerUuid: layer.uuid,
      sourceBeforeUuid,
      sourceAfterUuid,
    };

    if (identity.product === 'habitat' && identity.habitatClassification) {
      result.habitat[identity.habitatClassification] = target;

      if (identity.habitatClassification === 'l1') {
        result.availability.habitatL1 = true;
      } else {
        result.availability.habitatL2 = true;
      }

      result.availability.habitat = true;
      return;
    }

    if (identity.product === 'msa' && identity.msaComponent === 'overall') {
      result.msa = target;
      result.availability.msa = true;
    }
  });

  return result;
}

export function getPreferredHabitatClassification(
  layers: readonly IRasterLayer[],
  selectedLayer: IRasterLayer | null | undefined,
  availability: IBiomapInterventionAvailability,
): BiomapHabitatClassification {
  if (selectedLayer) {
    const identity = getBiomapRasterIdentity(selectedLayer);

    if (
      identity?.product === 'habitat' &&
      identity.habitatClassification &&
      isClassificationAvailable(identity.habitatClassification, availability)
    ) {
      return identity.habitatClassification;
    }
  }

  const visibleHabitat = layers
    .map((layer) => ({ layer, identity: getBiomapRasterIdentity(layer) }))
    .find(
      ({ layer, identity }) =>
        layer.isVisible &&
        identity?.product === 'habitat' &&
        !!identity.habitatClassification &&
        isClassificationAvailable(identity.habitatClassification, availability),
    )?.identity?.habitatClassification;

  if (visibleHabitat) {
    return visibleHabitat;
  }

  if (availability.habitatL1) {
    return 'l1';
  }

  return 'l2';
}

function isClassificationAvailable(
  classification: BiomapHabitatClassification,
  availability: IBiomapInterventionAvailability,
): boolean {
  return classification === 'l1'
    ? availability.habitatL1
    : availability.habitatL2;
}

function findMatchingSource(
  sources: readonly IRasterSource[],
  anchor: IRasterSource,
): IRasterSource | null {
  const exactKey = getSourceTemporalKey(anchor);

  if (!exactKey) {
    return null;
  }

  return (
    sources.find((source) => getSourceTemporalKey(source) === exactKey) ?? null
  );
}

function getSourceTemporalKey(source: IRasterSource): string {
  const rawDate = source.reference_date || source.start_date || source.end_date;

  if (!rawDate) {
    return '';
  }

  if (source.formatter_date === 'yyyy') {
    return rawDate.slice(0, 4);
  }

  if (source.formatter_date === 'yyyy-MM' || source.formatter_date === 'yyyy/MM') {
    return rawDate.slice(0, 7);
  }

  return rawDate.slice(0, 10);
}

export function extractBiomapInterventionGeometry(
  geoJson: unknown,
): BiomapInterventionGeometry | null {
  if (!geoJson || typeof geoJson !== 'object') {
    return null;
  }

  const polygonCoordinates: Polygon['coordinates'][] = [];
  collectPolygonCoordinates(geoJson as GeoJsonObject, polygonCoordinates);

  if (!polygonCoordinates.length) {
    return null;
  }

  if (polygonCoordinates.length === 1) {
    return {
      type: 'Polygon',
      coordinates: polygonCoordinates[0],
    };
  }

  return {
    type: 'MultiPolygon',
    coordinates: polygonCoordinates,
  };
}

function collectPolygonCoordinates(
  geoJson: GeoJsonObject,
  polygons: Polygon['coordinates'][],
): void {
  switch (geoJson.type) {
    case 'Polygon':
      polygons.push((geoJson as Polygon).coordinates);
      return;
    case 'MultiPolygon':
      polygons.push(...(geoJson as MultiPolygon).coordinates);
      return;
    case 'Feature': {
      const feature = geoJson as Feature;
      if (feature.geometry) {
        collectPolygonCoordinates(feature.geometry, polygons);
      }
      return;
    }
    case 'FeatureCollection':
      (geoJson as FeatureCollection).features.forEach((feature) =>
        collectPolygonCoordinates(feature, polygons),
      );
      return;
    case 'GeometryCollection':
      (geoJson as GeometryCollection).geometries.forEach((geometry) =>
        collectPolygonCoordinates(geometry, polygons),
      );
      return;
    default:
      return;
  }
}
